import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useAllAddresses } from 'app/network/address';
import { useAllGroups } from 'app/network/groups';
import ActivationModal, { useActivationModal } from 'app/screens/shared/activation-modal';
import type { Address } from 'app/types/address';
import { GroupType } from 'app/types/group-base';
import type { NavigationScreen } from 'app/types/navigation';
import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';

import {
  IconBriefcase,
  IconBuilding,
  IconChevronRight,
  IconHeart,
  IconHome,
  IconInfoCircle,
  IconMapPin,
  IconUser,
  IconUsersGroup,
  IconX,
} from '@tabler/icons-react-native';
import { PCImage } from 'app/design/primitives/image';
import { Modal } from 'app/design/primitives/modal';
import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useMyPlaces } from 'app/network/sharing-place';
import { Box, useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH, isMobileNative } from 'app/utils/constants';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { usePageTitle } from 'app/utils/web-helpers';
import { Image } from 'expo-image';
import { navigateToUserProfile } from '../billboard/navigator-helper';
import { isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';

export const getAddressIcon = (address: Pick<Address, 'icon'>, size?: number, color?: string) => {
  switch (address.icon) {
    case 1:
      return <IconHome size={size || 30} strokeWidth={1.5} color={color} />; // <AddressIcon1 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case 2:
      return <IconBriefcase size={size || 30} strokeWidth={1.5} color={color} />; // <AddressIcon2 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case 3:
      return <IconBuilding size={size || 30} strokeWidth={1.5} color={color} />; // <AddressIcon3 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case 4:
      return <IconHeart size={size || 30} strokeWidth={1.5} color={color} />; // <AddressIcon4 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case 5:
      return <IconMapPin size={size || 30} strokeWidth={1.5} color={color} />; // <AddressIcon5 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case 6:
      return <IconUsersGroup size={size || 30} strokeWidth={1.5} color={color} />; //<AddressIcon6 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case 7:
      return <IconUser size={size || 30} strokeWidth={1.5} color={color} />; // <AddressIcon7 height={height} width={width} color={theme.colors['surface.tertiary']} />;
    case null:
    default:
      return <IconHome size={size || 30} strokeWidth={1.5} color={color} />; //<AddressIcon1 height={height} width={width} color={theme.colors['surface.tertiary']} />;
  }
};

const MyClubsScreenComponent: NavigationScreen<'my-clubs'> = ({ navigation }) => {
  const theme = useTheme();
  const { t } = useScopedTranslation();

  const { data: addresses, isPending: isPendingAddresses } = useAllAddresses();
  const { data: groups, isPending: isPendingGroups } = useAllGroups();
  const { data: places, isPending: isPendingPlaces } = useMyPlaces();
  const visibleGroups = isWhiteLabel(WhiteLabel.HauteGaspesie)
    ? groups?.filter((group) => group.type === GroupType.sector)
    : groups;

  const { isActivationModalOpen, openActivationModal, showActivationButton } = useActivationModal();

  const onOpenActivationModal = () => {
    if (isWhiteLabel(WhiteLabel.HauteGaspesie)) {
      navigation.navigate('profile-overview', undefined, { pop: true });
    } else {
      openActivationModal();
    }
  };

  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const setPageTitle = usePageTitle('my-clubs');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  useRegisterLoading(isPendingAddresses, 'MyClubsScreen - fetching addresses');
  useRegisterLoading(isPendingGroups, 'MyClubsScreen - fetching groups');
  useRegisterLoading(isPendingPlaces, 'MyClubsScreen - fetching places');

  return (
    <Page title={t('title')}>
      <Pressable
        accessibilityLabel={t('infoButtonLabel')}
        accessibilityRole="button"
        onPress={() => setIsInfoOpen(true)}
        style={{ position: 'absolute', top: 10, right: 15, padding: 10 }}
      >
        <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={30} style={{}} />
      </Pressable>

      <Column gap="spacing.general.x4">
        {/* border bar but we only want show it in desktop */}
        {!isMobileNative ? (
          <View
            style={{
              height: 1,
              width: '100%',
              marginBottom: theme.spacing['spacing.general.x1'],
              marginTop: 0,
              backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
            }}
          />
        ) : null}

        <Text fontSize={16} variant="bodyBase">
          {t('neighbourhoodTitle')}
        </Text>

        {addresses?.map((address) => (
          <Button
            key={address.id}
            onPress={() => navigation.push('my-neighbourhood', { addressId: address.id })}
            style={{
              borderRadius: theme.borderRadii['radius.sm'],
              borderWidth: 1,
              borderColor: theme.colors['surface.tertiary-neutral'],
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              paddingVertical: theme.spacing['spacing.general.x2'],
            }}
            suffix={<IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
          >
            <Row alignItems="center" flexGrow={1} gap="spacing.general.x4">
              <Box
                backgroundColor="surface.brand.bold"
                paddingHorizontal="spacing.general.x2"
                paddingVertical="spacing.general.x2"
                borderRadius="radius.md"
              >
                {getAddressIcon(address, undefined, theme.colors.white)}
              </Box>
              <Text fontSize={18} fontWeight="600">
                {address.name ?? t('neighbourhoodClubName')}
              </Text>
            </Row>
          </Button>
        ))}

        <Button
          onPress={() => navigation.navigate('my-neighbourhood', { newAddressSource: 'my_clubs' }, { pop: true })}
          variant="secondary"
        >
          {t('createClubButton')}
        </Button>

        {showActivationButton || (visibleGroups && visibleGroups.length > 0) ? (
          <Column gap="spacing.general.x4">
            <View
              style={{
                height: 1,
                width: '100%',
                marginBottom: theme.spacing['spacing.general.x1'],
                marginTop: theme.spacing['spacing.general.x4'],
                backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
              }}
            />

            <Text fontSize={16} variant="bodyBase">
              {t('proTitle')}
            </Text>

            {visibleGroups?.map((group) => (
              <Button
                key={group.id}
                onPress={() =>
                  group.allowMembersListing
                    ? navigation.navigate('group-members', { groupIds: [group.id] }, { pop: true })
                    : navigation.navigate('my-plan', undefined, { pop: true })
                }
                style={{
                  borderRadius: theme.borderRadii['radius.sm'],
                  borderWidth: 1,
                  borderColor: theme.colors['surface.tertiary-neutral'],
                  paddingHorizontal: theme.spacing['spacing.general.x4'],
                  paddingVertical: theme.spacing['spacing.general.x2'],
                }}
                suffix={<IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
              >
                <Row alignItems="center" flexGrow={1} gap="spacing.general.x4">
                  <Image
                    source={{ uri: group.picture }}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme.colors['surface.disabled'],
                    }}
                  />
                  <Text fontSize={18} fontWeight="600">
                    {group.name}
                  </Text>
                </Row>
              </Button>
            ))}

            {showActivationButton ? (
              <Button onPress={onOpenActivationModal} variant="secondary">
                {t('addProClubButton')}
              </Button>
            ) : null}
          </Column>
        ) : null}

        {(places?.length ?? 0) > 0 ? (
          <Column gap="spacing.general.x4">
            <View
              style={{
                height: 1,
                width: '100%',
                marginBottom: theme.spacing['spacing.general.x1'],
                marginTop: 0,
                backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
              }}
            />
            <Text fontSize={16} variant="bodyBase">
              {t('sharingPlaceTitle')}
            </Text>

            {places?.map((place) => (
              <Button
                key={place.id}
                onPress={() => navigateToUserProfile(navigation, place.placeUserId)}
                style={{ padding: 0, borderRadius: 0 }}
                suffix={<IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
              >
                <Row alignItems="center" flexGrow={1} gap="spacing.general.x4">
                  <PCImage
                    source={{ uri: place.placeUser?.profile?.picture ?? undefined }}
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme.colors['surface.disabled'],
                    }}
                  />
                  <Text fontSize={18} fontWeight="600">
                    {place.placeUser?.profile?.firstName ?? t('sharingPlaceTitle')}
                  </Text>
                </Row>
              </Button>
            ))}
          </Column>
        ) : null}
      </Column>

      <ActivationModal isOpenState={isActivationModalOpen} />
      <Modal onClose={() => setIsInfoOpen(false)} open={isInfoOpen} transparent>
        <Modal.Card>
          <Pressable onPress={() => setIsInfoOpen(false)} style={{ padding: 10, margin: -10, width: 24 + 2 * 10 }}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} />
          </Pressable>
          <Column
            gap="spacing.general.x4"
            style={{ paddingHorizontal: 15, paddingBottom: theme.spacing['spacing.general.x12'] }}
          >
            <Text fontSize={16} fontWeight="bold" variant="bodyBase">
              {t('help.neighbourhoods.title')}
            </Text>
            <Text variant="bodyBase">{t('help.neighbourhoods.paragraph1')}</Text>
            <Text style={{ color: theme.colors['surface.brand.default'] }} variant="bodyBase">
              {t('help.neighbourhoods.paragraph2')}
            </Text>

            <View
              style={{
                height: 1,
                width: '100%',
                marginVertical: theme.spacing['spacing.general.x8'],
                backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
              }}
            />

            <Text fontSize={16} fontWeight="bold" variant="bodyBase">
              {t('help.pro.title')}
            </Text>
            <Text variant="bodyBase">{t('help.pro.paragraph1')}</Text>
          </Column>
        </Modal.Card>
      </Modal>
    </Page>
  );
};

export const MyClubsScreen: NavigationScreen<'my-clubs'> = withOptionalMetrics(
  'MyClubsScreen',
  withErrorBoundary(withDashboardLayout(MyClubsScreenComponent)),
);
